import { formatIsoDuration, EIsoTimeUnit } from 'src/utils/duration-iso.util'
import { EDurationUnit, ETriggerTiming, ETriggerUnit, TChatbotGeneralFormValues } from './chatbot-form.type'
import { ConversationNodes, THumanReviewConfig } from './chatbot-api.type'

function mapTriggerUnitToIso(unit: ETriggerUnit): EIsoTimeUnit {
  switch (unit) {
    case ETriggerUnit.HOURS:
      return EIsoTimeUnit.HOURS
    case ETriggerUnit.DAYS:
      return EIsoTimeUnit.DAYS
    case ETriggerUnit.WEEKS:
      return EIsoTimeUnit.WEEKS
    case ETriggerUnit.MONTHS:
      return EIsoTimeUnit.MONTHS
    default:
      throw new Error(`Unsupported trigger unit: ${unit}`)
  }
}

export function mapDurationUnitToIso(unit: EDurationUnit): EIsoTimeUnit {
  switch (unit) {
    case EDurationUnit.MINUTES:
      return EIsoTimeUnit.MINUTES
    case EDurationUnit.HOURS:
      return EIsoTimeUnit.HOURS
    case EDurationUnit.DAYS:
      return EIsoTimeUnit.DAYS
    default:
      throw new Error(`Unsupported duration unit: ${unit}`)
  }
}

function convertTriggerToIsoDuration(value: number, unit: ETriggerUnit, timing: ETriggerTiming): string {
  return formatIsoDuration({
    value,
    unit: mapTriggerUnitToIso(unit),
    sign: timing === ETriggerTiming.BEFORE ? '-' : '+',
  })
}

function convertDurationToIsoDuration(value: number, unit: EDurationUnit): string {
  return formatIsoDuration({
    value,
    unit: mapDurationUnitToIso(unit),
  })
}

function normalizeRequestHumanReview(
  value: TChatbotGeneralFormValues['options']['requestHumanReview'],
): THumanReviewConfig | null {
  if (!value) return null
  if (
    value.subject == null &&
    value.incompleteQuestionnaireDelay == null &&
    value.unreceivedSmsDelay == null
  ) {
    return null
  }
  return value
}

export type TChatbotApiPayload = {
  name?: string
  description?: string
  schedule?: {
    durationDiff: string
    sendAt?: string | null
    avoidedWeekdays: number[]
    medeventListIds: number[]
    avoidHolidays: boolean
  }
  options?: {
    automaticEnd?: {
      expireDuration: string
      expirationMessage?: string
    } | null
    humanReviewConfig?: THumanReviewConfig | null
    automaticRelaunchDelay?: string | null
    showUnreachedPatientInDashboardDelay?: string | null
  }
  nodes?: ConversationNodes
}

/**
 * Transforms chatbot form data to API payload format.
 * Converts duration values from number + unit to ISO 8601 duration strings.
 * @param formData - The form data from TChatbotGeneralFormValues
 * @returns The API payload with ISO duration strings
 */
export function transformChatbotFormToApiPayload(formData: TChatbotGeneralFormValues): TChatbotApiPayload {
  const triggerDelay = convertTriggerToIsoDuration(
    formData.schedule.triggerDelay,
    formData.schedule.triggerUnit,
    formData.schedule.triggerTiming,
  )

  type TOptionsType = NonNullable<TChatbotApiPayload['options']>

  let automaticEnd: TOptionsType['automaticEnd'] = null
  if (
    formData.options.automaticEnd &&
    formData.options.automaticEnd.expireDuration !== undefined &&
    formData.options.automaticEnd.expireDurationUnit
  ) {
    automaticEnd = {
      expireDuration: convertDurationToIsoDuration(
        formData.options.automaticEnd.expireDuration,
        formData.options.automaticEnd.expireDurationUnit,
      ),
      expirationMessage: formData.options.automaticEnd.expirationMessage,
    }
  }

  return {
    name: formData.name,
    description: formData.description,
    schedule: {
      durationDiff: triggerDelay,
      sendAt: formData.schedule.sendAt,
      avoidedWeekdays: [1, 2, 3, 4, 5, 6, 7].filter((day) => !formData.schedule.allowedDays.includes(day)),
      medeventListIds: formData.schedule.medeventListIds,
      avoidHolidays: formData.schedule.avoidHolidays,
    },
    options: {
      automaticEnd,
      humanReviewConfig: normalizeRequestHumanReview(formData.options.requestHumanReview),
      automaticRelaunchDelay: formData.options.automaticRelaunchDelay,
      showUnreachedPatientInDashboardDelay:
        formData.options.requestHumanReview?.incompleteQuestionnaireDelay ?? null,
    },
  }
}
